import React, { useState, useEffect, useCallback, useRef } from 'react';
import App from '../src/App';
import { normalizeLocationForUrl } from '../src/utils/urlUtils';

export { Page };

function Page({ initialData, initialCoordinates, initialTideData, locationText, initialWindData, initialShoreReference, is404, isRedirect, redirectUrl }) {
  const shouldRedirect = Boolean(isRedirect && redirectUrl);

  // Define hooks unconditionally to avoid React hydration/runtime errors.
  const [coordinates, setCoordinates] = useState(initialCoordinates || []);
  const [data, setData] = useState(initialData);
  const [tideData, setTideData] = useState(initialTideData);
  const [windData, setWindData] = useState(initialWindData);
  const [shoreReference, setShoreReference] = useState(initialShoreReference ?? null);
  const [locationName, setLocationName] = useState(locationText);
  const wsRef = useRef(null);
  const pendingLocationRef = useRef(null);
  const activeLocationRef = useRef(null);
  const latestRequestIdRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const shouldReconnectRef = useRef(false);

  useEffect(() => {
    if (!shouldRedirect) return;
    window.location.replace(redirectUrl);
  }, [shouldRedirect, redirectUrl]);

  const createWebSocket = useCallback(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    return new WebSocket(`${protocol}//${host}`);
  }, []);

  const connectWebSocket = useCallback(() => {
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
      return wsRef.current;
    }

    const socket = createWebSocket();
    wsRef.current = socket;

    socket.onopen = () => {
      console.log('WebSocket connected');
      const locationRequest = pendingLocationRef.current || activeLocationRef.current;
      if (locationRequest) {
        socket.send(JSON.stringify(locationRequest));
        pendingLocationRef.current = null;
      }
    };

    socket.onmessage = (event) => {
      let message;
      try {
        message = JSON.parse(event.data);
      } catch (error) {
        console.error('Invalid WebSocket response:', error);
        return;
      }

      const { initialData, tideData, windData, shoreReference, error, requestId } = message;
      if (requestId !== latestRequestIdRef.current) return;
      activeLocationRef.current = null;

      if (error) {
        console.error(error);
      } else {
        if (initialData) setData(initialData);
        if (tideData) setTideData(tideData);
        if (windData) setWindData(windData);
        if (shoreReference !== undefined) setShoreReference(shoreReference);
      }
    };

    socket.onclose = () => {
      if (wsRef.current === socket) wsRef.current = null;
      if (activeLocationRef.current?.requestId === latestRequestIdRef.current) {
        pendingLocationRef.current = activeLocationRef.current;
      }
      if (!shouldReconnectRef.current) return;
      console.log('WebSocket disconnected. Attempting to reconnect...');
      reconnectTimeoutRef.current = setTimeout(() => connectWebSocket(), 7000);
    };

    socket.onerror = (error) => {
      console.error('WebSocket error:', error);
      socket.close();
    };

    return socket;
  }, [createWebSocket]);

  useEffect(() => {
    if (is404 || shouldRedirect) return;
    // Only create WebSocket if we don't already have all the data we need
    if (!initialData || !initialTideData || !initialWindData || typeof initialShoreReference === 'undefined') {
      shouldReconnectRef.current = true;
      // Delay WebSocket connection to reduce Total Blocking Time
      let socket;
      let pingInterval;

      const timeoutId = setTimeout(() => {
        socket = connectWebSocket();

        pingInterval = setInterval(() => {
          if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      }, 2000); // Delay by 2 seconds to improve initial load performance and TBT

      return () => {
        shouldReconnectRef.current = false;
        clearTimeout(timeoutId);
        if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
        if (pingInterval) clearInterval(pingInterval);
        if (socket && wsRef.current === socket) {
          wsRef.current = null;
          socket.close();
        }
      };
    }
  }, [connectWebSocket, initialData, initialTideData, initialWindData]);

  const handleLocationChange = useCallback((newLat, newLong, spotName = null, updateHistory = true) => {
    setCoordinates([newLat, newLong]);
    setLocationName(spotName || null);
    setShoreReference(null);

    const request = {
      lat: newLat,
      lon: newLong,
      locationName: spotName,
      requestId: ++latestRequestIdRef.current,
    };
    activeLocationRef.current = request;
    shouldReconnectRef.current = true;

    const socket = connectWebSocket();
    if (socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(request));
      pendingLocationRef.current = null;
    } else {
      pendingLocationRef.current = request;
    }

    if (!updateHistory) return;

    const historyLocation = { latitude: newLat, longitude: newLong, name: spotName || null };
    if (spotName && spotName !== 'Custom Spot' && spotName !== 'Current Location') {
      const urlFriendlyName = normalizeLocationForUrl(spotName);
      window.history.pushState({ oceanorbLocation: historyLocation }, '', `/${urlFriendlyName}`);
    } else {
      window.history.pushState({ oceanorbLocation: historyLocation }, '', `/?lat=${newLat}&lon=${newLong}`);
    }
  }, [connectWebSocket]);

  useEffect(() => {
    const setHistoryLocation = (latitude, longitude, name) => {
      if (latitude == null || longitude == null) {
        latestRequestIdRef.current += 1;
        pendingLocationRef.current = null;
        activeLocationRef.current = null;
        setCoordinates([]);
        setLocationName(null);
        setData(null);
        setTideData(null);
        setWindData([]);
        setShoreReference(null);
        return;
      }

      handleLocationChange(latitude, longitude, name, false);
    };

    const syncLocationFromHistory = (event) => {
      const historyLocation = event.state?.oceanorbLocation;
      if (historyLocation) {
        setHistoryLocation(historyLocation.latitude, historyLocation.longitude, historyLocation.name);
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const latitude = Number(urlParams.get('lat'));
      const longitude = Number(urlParams.get('lon'));
      if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
        setHistoryLocation(latitude, longitude, null);
      } else {
        setHistoryLocation(null, null, null);
      }
    };

    window.history.replaceState({
      ...window.history.state,
      oceanorbLocation: initialCoordinates?.length === 2
        ? { latitude: initialCoordinates[0], longitude: initialCoordinates[1], name: locationText }
        : null,
    }, '');
    window.addEventListener('popstate', syncLocationFromHistory);
    return () => window.removeEventListener('popstate', syncLocationFromHistory);
  }, [handleLocationChange, initialCoordinates, locationText]);

  // Handle 404
  if (is404) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        textAlign: 'center',
        fontSize: '2.5rem',
        padding: '1rem',
        color: '#555',
      }}>
        <h1><b>404: Surf spot not found</b></h1>
        <br />
        <b><a href="/" style={{ fontSize: '2.5rem', textDecoration: 'underline' }}>Go Back</a></b>
      </div>
    );
  }

  // Handle redirect
  if (shouldRedirect) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        textAlign: 'center',
        fontSize: '2rem',
        padding: '1rem',
      }}>
        <h1>Redirecting...</h1>
        <p>If you are not redirected, <a href={redirectUrl} style={{ textDecoration: 'underline' }}>click here</a>.</p>
      </div>
    );
  }

  const onSearch = ({ latitude, longitude, text }) => {
    handleLocationChange(latitude, longitude, text);
  };
  
  return (
    <App 
      initialData={data} 
      initialTideData={tideData}
      windData={windData}
      shoreReference={shoreReference}
      latitude={coordinates[0]} 
      longitude={coordinates[1]} 
      onLocationChange={handleLocationChange}
      onSearch={onSearch}
      locationName={locationName}
    />
  );
}